// DSGVO-Service: Datenexport (Art. 20) + vollständige Konto-Löschung (Art. 17).
import {
  collection, getDocs, writeBatch, doc, deleteDoc, query, where,
} from '../lib/fsCompat';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import { db, storage } from '../firebase';
import { disablePush } from './pushClient';

// ── Datenexport ──────────────────────────────────────────────────────────────

/**
 * Sammelt alle User-Daten aus Firestore in ein JSON-Objekt.
 * Verzeichnis-Struktur: users/{uid}/transactions/*, users/{uid}/{andere Sub-Collections}
 */
export async function exportUserData(user) {
  if (!user?.uid) throw new Error('Nicht angemeldet');

  const result = {
    exportedAt: new Date().toISOString(),
    user: {
      uid: user.uid,
      email: user.email || null,
      displayName: user.displayName || null,
      providerId: user.providerData?.[0]?.providerId || null,
      createdAt: user.metadata?.creationTime || null,
    },
    collections: {},
  };

  // Bekannte Sub-Collections unter users/{uid}/
  const knownSubs = [
    'transactions', 'profile', 'settings', 'walletGroups',
    'manualPrices', 'importReviews', 'importCorrections',
    'priceHistory', 'redactedHashes', 'alerts', 'alertHits',
    'exchangeConnections', 'pushTokens',
  ];

  for (const name of knownSubs) {
    try {
      const snap = await getDocs(collection(db, 'users', user.uid, name));
      if (snap.empty) continue;
      // exchangeConnections: Secrets aus dem Export entfernen — Plaintext gibt's nie,
      // aber wir geben nicht mal den encrypted Blob raus. Nur Metadaten.
      if (name === 'exchangeConnections') {
        result.collections[name] = snap.docs.map(d => {
          const { keyBlob, secretBlob, passBlob, ...meta } = d.data();
          return { id: d.id, ...meta };
        });
      } else if (name === 'pushTokens') {
        // FCM-Token NICHT exportieren — er wäre für niemanden außer dem Server
        // nutzbar und ist defacto ein Device-Identifier. Nur Metadaten ausgeben.
        result.collections[name] = snap.docs.map(d => {
          const { token, ...meta } = d.data();
          return { id: d.id, ...meta };
        });
      } else {
        result.collections[name] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
    } catch {
      // Sub-Collection existiert nicht oder ist nicht lesbar — überspringen
    }
  }

  return result;
}

/**
 * Triggert einen Browser-Download der JSON-Datei.
 */
export function downloadUserDataAsJson(data, fileName) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `red-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Konto-Löschung ───────────────────────────────────────────────────────────

/**
 * Löscht alle User-Daten aus Firestore Sub-Collections, Storage-Uploads
 * und schließlich den Firebase-Auth-Account selbst.
 *
 * WICHTIG: `deleteUser` kann fehlschlagen mit `auth/requires-recent-login`.
 * In dem Fall muss der User sich neu anmelden und es erneut versuchen.
 */
export async function deleteAccountCompletely(user) {
  if (!user?.uid) throw new Error('Nicht angemeldet');
  const uid = user.uid;

  // 1) Firestore: alle bekannten Sub-Collections leeren
  const knownSubs = [
    'transactions', 'profile', 'settings', 'walletGroups',
    'manualPrices', 'importReviews', 'importCorrections',
    'priceHistory', 'redactedHashes', 'alerts', 'alertHits',
    'exchangeConnections', 'pushTokens',
  ];
  for (const name of knownSubs) {
    try {
      const colRef = collection(db, 'users', uid, name);
      const snap = await getDocs(colRef);
      const BATCH_SIZE = 400;
      for (let i = 0; i < snap.docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + BATCH_SIZE).forEach(d => batch.delete(doc(colRef, d.id)));
        await batch.commit();
      }
    } catch {
      // Collection nicht vorhanden / nicht lesbar — überspringen
    }
  }

  // Eigene globale exchangeTemplates (createdBy == uid) löschen — liegen NICHT
  // unter users/{uid}, sondern in der top-level Collection (Rules erlauben dem
  // Ersteller delete). Sonst bliebe ein vom User erzeugtes Template zurück.
  try {
    const tplSnap = await getDocs(query(collection(db, 'exchangeTemplates'), where('createdBy', '==', uid)));
    for (let i = 0; i < tplSnap.docs.length; i += 400) {
      const batch = writeBatch(db);
      tplSnap.docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  } catch { /* keine eigenen Templates / kein Zugriff — überspringen */ }

  // Top-level, uid-verschlüsselte Marker außerhalb von users/{uid} — DSGVO:
  // nichts mit Personenbezug zurücklassen. (betaSignups = Zähler-Marker,
  // betaGrants = owner-gesetzte Beta-Freischaltung; beide jetzt self-deletable.)
  try { await deleteDoc(doc(db, 'betaSignups', uid)); } catch {}
  try { await deleteDoc(doc(db, 'betaGrants', uid)); } catch {}
  try { await deleteDoc(doc(db, 'memberNumbers', uid)); } catch {}

  // Optional: das User-Doc selbst löschen (falls direkt unter users/{uid})
  try { await deleteDoc(doc(db, 'users', uid)); } catch {}

  // 2) FCM-Push-Token widerrufen (Client + Server) — sonst bleibt eine aktive
  // Push-Subscription für ein gelöschtes Konto bestehen.
  try { await disablePush(); } catch {}

  // 3) Storage: alle Import-Dateien des Users löschen
  try {
    const importsRef = ref(storage, `users/${uid}/imports`);
    const list = await listAll(importsRef);
    await Promise.all(list.items.map(item => deleteObject(item).catch(() => null)));
  } catch {
    // kein Storage-Inhalt oder Zugriff verweigert — überspringen
  }

  // 4) LocalStorage / SessionStorage clearen
  try {
    Object.keys(localStorage).forEach(k => {
      if (k.startsWith('red_')) localStorage.removeItem(k);
    });
  } catch {}

  // 5) Firebase-Auth-Account löschen (zuletzt — sonst keine Schreibrechte mehr).
  // Native: über das Auth-Plugin (auth.currentUser/JS-SDK-User existiert dort nicht).
  if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    await FirebaseAuthentication.deleteUser();
  } else {
    await deleteUser(user);
  }
}
